import { Dropdown, Table, Form } from "react-bootstrap";
import {
  ExportIcon,
  FilterTwoIcon,
  ChartPieSliceTitleIcon,
  ExcelIcon,
  PDFIcon,
  DownloadIcon,
  EditIcon,
  DeleteIcon,
} from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import CachedIcon from "mdi-react/CachedIcon";
import Select from "react-select";
import useDebounce, {
  useCurrencies,
  useLocalStorage,
  useQueryParams,
  useScrollTop,
} from "../../utils/hooks";
import { services } from "../../config";
import { reportActions } from "../../utils/reactQueryActions";
import { useMutation, useQuery } from "react-query";
import currency from "currency.js";
import { format, parse } from "date-fns";
import ReactPaginate from "react-paginate";
import { allAcountDetailIDs, fetchActionsUtil } from "../../utils/helpers";
import RsDateRangePicker from "./../utils/RsDateRangePicker";
import queryString from "query-string";
import { Link, useNavigate } from "react-router-dom";
import { useStoreActions, useStoreState } from "easy-peasy";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { useAuth } from "../../hooks/useAuth";
import PageHeader from "../PageHeader";
import AddAccounts from "../modals/AddAccounts";
import { toUpper } from "lodash";
import { IsPrivileged } from "../DisplayChildElement";

// ----------------------------------------------------
import { CSVLink } from "react-csv";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";
import { isEmpty } from "lodash";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { read, utils } from "xlsx";
import ConfirmDialog from "../ConfirmDialogue";
import * as yup from "yup";
import { toast } from "react-toastify";
import EditAccounts from "../modals/EditAccounts";
import DeleteAccounts from "../modals/DeleteAccounts";
import { memo } from "react";
import EditSubAccountModal from "../ChartOfAccount/Modals/EditSubAccountModal";

export default function ChartOfAccountReport() {
  useScrollTop();
  const navigate = useNavigate();

  const initialFilterParams = useMemo(
    () => ({
      usage: "chart of accounts",
      calculateRunningBalance: true,
      Type: "",
      DetailType: "",
    }),
    []
  );

  const {
    deploymentCurrencies: currenciesOptions,
    prevailingCurrency,
  } = useCurrencies();
  const { backendUrl, token } = useAuth();
  const [showFilter, setShowFilter] = useLocalStorage("showFilter", true);
  const [showCreateNewAccountModal, setShowCreateNewAccountModal] = useState(
    false
  );
  const [selectedAccountToEdit, setSelectedAccountToEdit] = useState();
  const [selectedAccountToDelete, setSelectedAccountToDelete] = useState();
  const [selectedSubAccountToEdit, setSelectedSubAccountToEdit] = useState();

  const [excelData, setExcelData] = useState([]);
  const CSVLinkRef = useRef(null);
  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
    currency: "",
    convertCurrency: prevailingCurrency,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterParams]);

  const getExpenses = useCallback(
    async debouncedqueryParams => {
      try {
        let response = await fetch(
          `${backendUrl}/api/journal/journal-list?${queryString.stringify(
            debouncedqueryParams
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "Application/json",
              "Content-Type": "Application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          response = await response.json();
          throw new Error(response.message);
        }

        const { data } = await response.json();
        data.accountTypes = [
          {
            label: "All Account Types",
            value: "",
          },
          ...data.accountTypes.map(el => ({
            ...el,
            label: toUpper(el.Type),
            value: el.Type,
          })),
        ];
        data.accountDetailTypes = [
          {
            label: "All Detail Types",
            value: "",
          },
          ...data.accountDetailTypes.map(el => ({
            ...el,
            label: toUpper(el.DetailType),
            value: el.DetailType,
          })),
        ];
        return data;
      } catch (error) {
        console.error("Error fetching expenses:", error);
        throw error;
      }
    },
    [backendUrl]
  );

  const { data = { count: 0, records: [] }, refetch } = useQuery(
    [reportActions.SALES_INVENTORY, queryParams],
    () => getExpenses(queryParams),
    {
      keepPreviousData: true,
      staleTime: 30000, // Added staleTime to reduce refetches
    }
  );

  // Memoize accountTypes and accountDetailTypes to prevent unnecessary re-renders
  const accountTypes = useMemo(() => data?.accountTypes || [], [
    data?.accountTypes,
  ]);
  const accountDetailTypes = useMemo(() => data?.accountDetailTypes || [], [
    data?.accountDetailTypes,
  ]);

  const handleFilterParamsChange = useCallback(e => {
    const { name, type, value, checked } = e.target;
    setFilterParams(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSearchQueryChange = useCallback(
    e => {
      const { name, value } = e.target;
      setQueryParams(prev => ({
        ...prev,
        [name]: value,
      }));
    },
    [setQueryParams]
  );

  const handleTypeChange = useCallback(selected => {
    setFilterParams(prev => ({
      ...prev,
      page: 1,
      Type: selected.value,
    }));
  }, []);

  const handleDetailTypeChange = useCallback(selected => {
    setFilterParams(prev => ({
      ...prev,
      page: 1,
      DetailType: selected.value,
    }));
  }, []);

  const handleCurrencyChange = useCallback(selected => {
    setFilterParams(prev => ({
      ...prev,
      page: 1,
      currency: selected?.value,
    }));
  }, []);

  const handleConvertCurrencyChange = useCallback(({ value }) => {
    setFilterParams(prev => ({
      ...prev,
      page: 1,
      convertCurrency: value,
    }));
  }, []);

  const { limit, page, ...rest } = queryParams;
  const exportUrlParams = useMemo(() => queryString.stringify(rest), [rest]);

  const onDownloadExcelData = useCallback(async () => {
    try {
      let exData = await fetchExcelData(
        `${backendUrl}/api/journal/journal-list?${exportUrlParams}`,
        "GET"
      );

      const company = exData.data?.company?.CompName;
      const date = "";

      let records = exData.data.records;
      let dataForExcel = [];

      for (let el of records) {
        const title = [
          `${findRange(el?.AccountType)[0] || ""} - ${el?.AccountType || ""}`,
          "",
          "",
          "",
        ];

        if (
          !isEmpty(el.subAccounts) &&
          el.subAccounts.find(el => !isEmpty(el.subAccountsRecords))
        ) {
          if (!isEmpty(title)) {
            dataForExcel.push(title);
          }
          for (let sub of el.subAccounts) {
            if (!isEmpty(sub.subAccountsRecords)) {
              if (sub?.AccountType || sub?.AccountDesc) {
                dataForExcel.push([
                  `${sub?.AccountType || ""} - ${sub?.AccountDesc || ""}`,
                  "",
                  "",
                  "",
                ]);
              }

              dataForExcel.push([
                "Account ID",
                "Title",
                "Account Type",
                "Balance",
              ]);

              for (let subAcc of sub.subAccountsRecords) {
                dataForExcel.push([
                  subAcc.AccountID,
                  subAcc.Description,
                  subAcc.accountTypeForReport,
                  currency(subAcc.Balance, {
                    symbol: "",
                  }).format(),
                ]);
              }
            }
          }
        }
      }

      const headers = [];

      exData = [
        [company],
        ["Chart of Accounts"],
        [date],
        [""],
        headers,
        ...dataForExcel,
        [""],
      ];

      setExcelData(exData);
    } catch (error) {
      console.error("Error downloading Excel data:", error);
      toast.error("Failed to download Excel data");
    }
  }, [backendUrl, exportUrlParams, fetchExcelData]);

  const openList = useCallback(
    (e, el) => {
      if (e.detail === 2) {
        navigate(
          `/account-setup/account-list?AccountID=${
            el?.AccountID
          }&Description=${encodeURIComponent(el?.Description)}`
        );
      }
    },
    [navigate]
  );

  const findRange = useCallback(account => {
    const data = [];

    allAcountDetailIDs.every(el => {
      if (el.data.some(d => d === account)) {
        data.push(Number(el.num1), Number(el.num2));
        return false;
      }
      return true;
    });

    return data;
  }, []);

  const addStockAccountList = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/invexcloud/add-accounts`,
        "POST",
        token,
        payload
      ),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        refetch();
      },
      onError: error => {
        console.error(error);
        toast.error(`Unable to perform action: ${error}`);
      },
    }
  );

  const addStockAccountList2 = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/invexcloud/delete-accounts`,
        "POST",
        token,
        payload
      ),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        refetch();
      },
      onError: error => {
        console.error(error);
        toast.error(`Unable to perform action: ${error}`);
      },
    }
  );

  function handleFile({ eventData: e, type }) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async function(e) {
        try {
          const workbook = read(e.target.result, {
            cellDates: true,
            dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
          });
          let rows = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            {
              defval: "",
              dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
            }
          );

          if (
            !(await ConfirmDialog({
              title: "Are you sure?",
              description: "You want to import",
            }))
          ) {
            return;
          }

          let schema = yup.object().shape({
            items: yup.array().of(
              yup.object({
                Account_ID: yup.string(),
                Account_Name: yup.string().required(),
                Account_Type: yup.string().required(),
                Type_Detail: yup.string().required(),
              })
            ),
          });

          await schema.validate({ items: rows });

          // Clean Up
          rows = rows.map(el => ({
            AccountID: String(el.Account_ID),
            Description: el.Account_Name,
            Account_Desc: el.Account_Name,
            Type: el.Account_Type.toUpperCase(),
            DetailType: el.Type_Detail.toUpperCase(),
          }));

          addStockAccountList.mutate({ accounts: rows });
        } catch (err) {
          console.error(err);
          toast.error(err.name);
          toast.error(JSON.stringify(err.errors));
        }
      };
      reader.readAsArrayBuffer(file);
    }
    e.target.value = "";
  }

  function handleCleanUpFile({ eventData: e, type }) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async function(e) {
        try {
          const workbook = read(e.target.result, {
            cellDates: true,
            dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
          });
          let rows = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            {
              defval: "",
              dateNF: "yyyy-MM-dd HH:mm:ss.SSS",
            }
          );

          if (
            !(await ConfirmDialog({
              title: "Are you sure?",
              description: "You want to import",
              type: "danger",
            }))
          ) {
            return;
          }

          let schema = yup.object().shape({
            items: yup.array().of(
              yup.object({
                Description: yup.string().required(),
                Keep: yup.string().required(),
              })
            ),
          });

          await schema.validate({ items: rows });

          // Clean Up
          rows = rows
            .map(el => ({
              ...el,
              Keep: Boolean(parseInt(el.Keep)),
            }))
            .filter(el => !el.Keep);

          addStockAccountList2.mutate({ accounts: rows });
        } catch (err) {
          console.error(err);
          toast.error(err.name);
          toast.error(JSON.stringify(err.errors));
        }
      };
      reader.readAsArrayBuffer(file);
    }
    e.target.value = "";
  }

  const handleEdit = useCallback(subAcc => {
    return String(subAcc.AccountID).length <= 3
      ? toast.error("Built-in Accounts cannot be modified")
      : setSelectedAccountToEdit(subAcc);
  }, []);

  const handleDelete = useCallback(subAcc => {
    return String(subAcc.AccountID).length <= 3
      ? toast.error("Built-in Accounts cannot be modified")
      : setSelectedAccountToDelete(subAcc);
  }, []);

  const handleSubAccountEdit = useCallback(subAcc => {
    setSelectedSubAccountToEdit(subAcc);
  }, []);

  // Window Events Optimization
  const fileInputRef = useRef(null);
  const cleanupFileInputRef = useRef(null);

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCleanupFileClick = useCallback(() => {
    cleanupFileInputRef.current?.click();
  }, []);

  // Memoize dropdown items to reduce re-renders
  const exportDropdownItems = useMemo(
    () => (
      <>
        <Dropdown.Item
          as="div"
          onClick={onDownloadExcelData}
          className="p-cursor"
        >
          Excel <ExcelIcon color="#008000" />
        </Dropdown.Item>
        <Dropdown.Item as="div">
          <a
            href={`${backendUrl}/api/journal/pdf/journal-list?${exportUrlParams}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            PDF
            <PDFIcon color="#ff0000" />
          </a>
        </Dropdown.Item>
      </>
    ),
    [onDownloadExcelData, backendUrl, exportUrlParams]
  );

  const dotsDropdownItems = useMemo(
    () => (
      <>
        <Dropdown.Item as="div">
          <a
            href={`/excel_templates/INVEX_INSERT_ACCOUNTS_LIST_TEMPLATE.xlsx`}
            target="_blank"
            rel="noopener noreferrer"
            title="Download"
            download=""
          >
            <DownloadIcon /> Template
          </a>
        </Dropdown.Item>

        <Dropdown.Item
          as="div"
          className="p-cursor"
          title="Import"
          onClick={handleFileClick}
        >
          <ExcelIcon color="#008000" /> Import
        </Dropdown.Item>

        <Dropdown.Item
          as="div"
          className="p-cursor"
          title="Import"
          onClick={handleCleanupFileClick}
        >
          <ExcelIcon color="#008000" /> Bulk Delete
        </Dropdown.Item>

        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          onChange={e => handleFile({ eventData: e })}
          accept=".xlsx"
        />

        <input
          ref={cleanupFileInputRef}
          type="file"
          className="d-none"
          onChange={e => handleCleanUpFile({ eventData: e })}
          accept=".xlsx"
        />
      </>
    ),
    [handleFileClick, handleCleanupFileClick]
  );

  // Virtualized row rendering for large datasets
  const renderAccountRecords = useCallback(() => {
    return data?.records?.map((el, index) => {
      if (
        isEmpty(el.subAccounts) ||
        !el.subAccounts.find(el => !isEmpty(el.subAccountsRecords))
      ) {
        return null;
      }

      return (
        <div className="mb-5" key={`${index}_${"1"}`}>
          {el.AccountType === "CASH AND CASH EQUIVALENT" ? (
            <p className="h4">
              <b>{findRange(el.AccountType)[0]} - CURRENT ASSET</b>
            </p>
          ) : ["ACCOUNTS RECEIVABLE", "CURRENT ASSET"].includes(
              el.AccountType
            ) ? null : (
            <p className="h4">
              <b>
                {findRange(el.AccountType)[0]} - {el.AccountType}
              </b>
            </p>
          )}

          <SubAccountsTable
            accountData={el}
            index={index}
            openList={openList}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleSubAccountEdit={handleSubAccountEdit}
          />
        </div>
      );
    });
  }, [
    data?.records,
    findRange,
    handleDelete,
    handleEdit,
    openList,
    handleSubAccountEdit,
  ]);

  return (
    <IsPrivileged roleName="Chart of Account">
      <main className="inner-page-outlet">
        <PageHeader
          name="Chart of Accounts"
          description="Account List"
          icon={<ChartPieSliceTitleIcon />}
        />

        <main className="cash-book">
          <div className="content">
            <div className="content-main">
              <div className="content-body">
                <header className="align-items-start pt-5">
                  <div className="d-flex gap-3 flex-grow-1 align-items-center z-10 w-50">
                    <h1>
                      <button
                        onClick={() => refetch()}
                        className="btn text-primary"
                      >
                        <CachedIcon />
                      </button>
                    </h1>

                    <div className="global-search-area col-md-4 text-nowrap">
                      <MagnifyIcon />
                      <Form.Control
                        className=""
                        name="Description"
                        value={filterParams.Description || ""}
                        onChange={handleFilterParamsChange}
                        placeholder="Search Description..."
                      />
                    </div>
                    <div className="col-md-3">
                      <Select
                        classNamePrefix={"form-select"}
                        options={accountTypes}
                        value={accountTypes.find(
                          el => el.value === filterParams.Type
                        )}
                        onChange={handleTypeChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <Select
                        classNamePrefix={"form-select"}
                        options={accountDetailTypes}
                        value={accountDetailTypes.find(
                          el => el.value === filterParams.DetailType
                        )}
                        onChange={handleDetailTypeChange}
                      />
                    </div>
                  </div>
                  <div className="actions mr-4 flex-wrap justify-content-end w-50">
                    <Form.Group className="position-relative">
                      <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                        Filter By
                      </Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        placeholder="All Currencies"
                        isSearchable={true}
                        options={currenciesOptions}
                        value={currenciesOptions.find(
                          el => el.value == filterParams.currency
                        )}
                        onChange={handleCurrencyChange}
                        isClearable={true}
                        menuPosition="fixed"
                      />
                    </Form.Group>
                    <Form.Group className="position-relative">
                      <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                        Report In{" "}
                      </Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        placeholder="Select Currency"
                        isSearchable={true}
                        options={currenciesOptions}
                        value={currenciesOptions.find(
                          el => el.value === filterParams.convertCurrency
                        )}
                        onChange={handleConvertCurrencyChange}
                        menuPosition="fixed"
                      />
                    </Form.Group>{" "}
                    <br />
                    <button
                      onClick={() => setShowCreateNewAccountModal(true)}
                      className="btn btn-primary"
                    >
                      Create New
                    </button>
                    <Link
                      to={"/account-setup/chart-of-account-report"}
                      className="btn btn-light-blue"
                    >
                      Show Running Balance
                    </Link>
                    <CSVLink
                      className="btn print d-none"
                      filename={`Chart_of_Accounts_(${format(
                        new Date(),
                        "dd-MMM-yyyy hh:mm:ss a"
                      )}).csv`}
                      data={excelData}
                      ref={CSVLinkRef}
                    />
                    <Dropdown>
                      <Dropdown.Toggle
                        variant=""
                        className="btn print"
                        disabled={isfetchingExcel}
                        bsPrefix=""
                      >
                        Export
                        <ExportIcon color="#008000" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{
                          strategy: "fixed",
                        }}
                        renderOnMount
                        className="text-center"
                      >
                        {exportDropdownItems}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant=""
                        className="btn print"
                        bsPrefix=""
                      >
                        <DotsVerticalIcon />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{
                          strategy: "fixed",
                        }}
                        renderOnMount
                        className="dropdown-with-icons"
                      >
                        {dotsDropdownItems}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </header>
                <div className="px-4">{renderAccountRecords()}</div>
              </div>
            </div>
          </div>
        </main>

        {showCreateNewAccountModal && (
          <AddAccounts
            show={showCreateNewAccountModal}
            onHide={() => setShowCreateNewAccountModal(false)}
            refetch={refetch}
          />
        )}

        {selectedAccountToEdit && (
          <EditAccounts
            selectedAccountToEdit={selectedAccountToEdit}
            show={selectedAccountToEdit}
            onHide={() => setSelectedAccountToEdit()}
            refetch={refetch}
          />
        )}

        {selectedAccountToDelete && (
          <DeleteAccounts
            selectedAccountToDelete={selectedAccountToDelete}
            show={selectedAccountToDelete}
            onHide={() => setSelectedAccountToDelete()}
            refetch={refetch}
          />
        )}

        {selectedSubAccountToEdit && (
          <EditSubAccountModal
            old={selectedSubAccountToEdit}
            show={selectedSubAccountToEdit}
            onHide={() => setSelectedSubAccountToEdit()}
            refetch={refetch}
          />
        )}
      </main>
    </IsPrivileged>
  );
}

const SubAccountsTable = memo(
  ({
    accountData,
    index,
    openList,
    handleEdit,
    handleDelete,
    handleSubAccountEdit,
  }) => {
    return (
      <>
        {accountData.subAccounts?.map(sub => {
          if (isEmpty(sub.subAccountsRecords)) {
            return null;
          }

          return (
            <div className="mb-3" key={`${index}_${sub.AccountDesc}`}>
              <p className="mb-2">
                <b>
                  {sub.AccountDesc
                    ? `${sub.AccountDesc}`
                    : accountData.AccountType}
                </b>
              </p>
              <Table borderless striped hover className="product-table">
                <thead>
                  <tr>
                    <th scope="col" className="col-1">
                      {""}
                    </th>
                    <th scope="col" className="col-1">
                      {""}
                    </th>
                    <th scope="col" className="col-3">
                      Title
                    </th>
                    <th scope="col" className="col-2">
                      Account Type
                    </th>
                    <th scope="col" className="col-2">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sub?.subAccountsRecords?.map(subAcc => (
                    <tr
                      key={`${index}_${subAcc.AccountID}`}
                      className="p-cursor"
                      onClick={e => openList(e, subAcc)}
                    >
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant=""
                            className="bg-white border-0"
                            bsPrefix="print more"
                          >
                            <DotsVerticalIcon />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            renderOnMount={false}
                            className="dropdown-with-icons"
                          >
                            <Dropdown.Item
                              as="button"
                              onClick={() => handleEdit(subAcc)}
                            >
                              <EditIcon />
                              Edit
                            </Dropdown.Item>

                            {["FIXED ASSET", "NON-CURRENT ASSETS"].includes(
                              subAcc.Type
                            ) && (
                              <Dropdown.Item
                                as="button"
                                onClick={() => {
                                  handleSubAccountEdit(subAcc);
                                }}
                              >
                                <EditIcon />
                                Change Depreciation Account
                              </Dropdown.Item>
                            )}

                            <Dropdown.Item
                              as="button"
                              onClick={() => handleDelete(subAcc)}
                            >
                              <DeleteIcon />
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>{subAcc.AccountID}</td>
                      <td>{subAcc.Description}</td>
                      <td>{subAcc.accountTypeForReport}</td>
                      <td>
                        {currency(subAcc.Balance, {
                          symbol: "",
                        }).format()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Deep comparison to prevent unnecessary re-renders
    return (
      prevProps.accountData.AccountType === nextProps.accountData.AccountType &&
      prevProps.index === nextProps.index &&
      JSON.stringify(prevProps.accountData.subAccounts) ===
        JSON.stringify(nextProps.accountData.subAccounts)
    );
  }
);
